import { kartoyImages } from '@mobile/assets/kartoy'
import { PillButton } from '@mobile/components/kartoy/pill-button'
import {
  apiGetTransaction,
  fetchCards,
  fetchTransactions,
  issueCard,
  selectedCardIssue,
  selectedCards,
} from '@shared-core/features/backend'
import { useMobileConfig } from '@shared-core/features/config'
import {
  formatOperationMoney,
  type Operation,
  type OperationKind,
  OperationStatus,
  parseBackendOperation,
} from '@shared-core/features/operations'
import { useI18N } from '@shared-core/hooks/use-i18n'
import { type RootRouteProps, ScreensList, useNavigation, useRoute } from '@shared-core/navigation'
import { useAppDispatch, useAppSelector } from '@shared-core/store'
import { formatPhoneDisplay } from '@shared-core/utils/phone'
import { AppBankCard } from '@shared-ui/components/app/bank-card'
import { AppIcon } from '@shared-ui/components/ui/app-icon'
import { FastImage } from '@shared-ui/components/ui/fast-image'
import { Header } from '@shared-ui/components/ui/header'
import { ScreenWrapper } from '@shared-ui/components/ui/screen-wrappers/screen-wrapper'
import { StatusNotice } from '@shared-ui/components/ui/status-notice'
import { GText } from '@shared-ui/components/ui/text'
import { useClipboard } from '@shared-ui/platform/hooks/use-clipboard'
import { handleOpenLink } from '@shared-ui/platform/link'
import { type ReactNode, useEffect, useMemo, useState } from 'react'
import { Pressable, View } from 'react-native'
import Animated, { FadeInDown } from 'react-native-reanimated'

import locales from './index.i18n.json'
import { useCreateStyles } from './styles'

type ValueTone = 'primary' | 'success' | 'warning' | 'critical'

type DetailRowProps = {
  children?: ReactNode
  delay: number
  label: string
  value?: string
  valueTone?: ValueTone
}

type OperationKindProps = { kind: OperationKind; operation?: Operation }
type TransactionHeroProps = { operation: Operation }
type TransactionDetailsProps = { operation: Operation }

function DetailRow({ children, delay, label, value, valueTone = 'primary' }: DetailRowProps) {
  const styles = useCreateStyles()
  const valueStyles = {
    primary: styles.rowValue,
    success: styles.rowValueSuccess,
    warning: styles.rowValueWarning,
    critical: styles.rowValueCritical,
  }

  return (
    <Animated.View entering={FadeInDown.delay(delay).duration(360)} style={styles.row}>
      <GText style={styles.rowLabel}>{label}</GText>
      {children ?? (
        <GText fw={500} style={valueStyles[valueTone]}>
          {value}
        </GText>
      )}
    </Animated.View>
  )
}

type BankAvatarProps = { name?: string; isSmall?: boolean }

function BankAvatar({ name, isSmall = false }: BankAvatarProps) {
  const styles = useCreateStyles()
  const isVtb = name?.toUpperCase().includes('ВТБ') || name?.toLowerCase().includes('vtb')
  return (
    <View style={[styles.bankAvatar, isSmall && styles.bankAvatarSmall]}>
      <FastImage
        source={isVtb ? kartoyImages.vtbAvatarLogo : kartoyImages.tBankAvatarLogo}
        style={styles.bankAvatarLogo}
        resizeMode="contain"
      />
    </View>
  )
}

function TransactionAvatar({ kind, operation }: OperationKindProps) {
  const styles = useCreateStyles()
  const { t } = useI18N(locales)

  if (kind === 'withdraw') return <BankAvatar name={operation?.bankName} />

  if (kind === 'card-issue')
    return (
      <View style={styles.avatar}>
        <AppIcon icon="card" size={40} isSecondary />
      </View>
    )

  if (kind === 'topup') {
    return (
      <View style={styles.avatar}>
        <AppIcon icon="topup" size={40} isSecondary />
      </View>
    )
  }

  return (
    <View style={styles.avatar}>
      <GText fw={600} style={styles.avatarLetter}>
        {(operation?.subtitle?.trim() || operation?.title?.trim() || t('merchant'))[0]}
      </GText>
    </View>
  )
}

function TransactionHero({ operation }: TransactionHeroProps) {
  const styles = useCreateStyles()
  const { t } = useI18N(locales)
  const { kind } = operation
  const isPurchase = kind === 'purchase' || kind === 'cancelled-purchase'
  let amountStyle = styles.amount

  if (operation.status === OperationStatus.Pending) amountStyle = styles.amountPending

  if (operation.status === OperationStatus.Failed)
    amountStyle = kind === 'card-issue' ? styles.amountPending : styles.amountCancelled

  if (operation.status === OperationStatus.Completed && kind === 'topup') amountStyle = styles.amountSuccess

  const title = isPurchase ? operation.subtitle?.trim() || operation.title.trim() || t('merchant') : t(`${kind}-title`)

  return (
    <Animated.View entering={FadeInDown.delay(60).duration(420)} style={styles.hero}>
      <TransactionAvatar kind={kind} operation={operation} />
      <View style={styles.summary}>
        <GText fw={600} style={amountStyle}>
          {operation.fiatAmount}
        </GText>
        <GText style={styles.subtitle}>{title}</GText>
      </View>
    </Animated.View>
  )
}

function TransactionFacts({ operation }: TransactionDetailsProps) {
  const styles = useCreateStyles()
  const { t } = useI18N(locales)
  const { kind } = operation
  const amount =
    operation.debitAmount && operation.billingCurrency
      ? formatOperationMoney(operation.debitAmount, operation.billingCurrency)
      : '—'
  const rate = operation.exchangeRate
    ? `$1 = ${formatOperationMoney(operation.exchangeRate, operation.billingCurrency ?? 'RUB')}`
    : '—'

  if (kind === 'purchase' || kind === 'cancelled-purchase') {
    return (
      <View style={styles.detailGroup}>
        <DetailRow delay={180} label={t('purchase-amount')} value={amount} />
        {operation.status === OperationStatus.Completed && (
          <DetailRow delay={220} label={t('exchange-rate')} value={rate} />
        )}
        <CardIssueFacts operation={operation} />
      </View>
    )
  }
  return (
    <View style={styles.detailGroup}>
      <DetailRow delay={180} label={t(kind === 'withdraw' ? 'to-receive' : 'to-pay')} value={amount} />
      <DetailRow delay={220} label={t('at-rate')} value={rate} />
    </View>
  )
}

function CardIssueFacts({ operation }: TransactionDetailsProps) {
  const styles = useCreateStyles()
  const { t } = useI18N(locales)
  const cards = useAppSelector(selectedCards)
  const card = useMemo(() => cards?.find((item) => item.id === operation.cardId), [cards, operation.cardId])
  const cardType = card?.templateId ?? card?.type
  const templateId = cardType === 'platinum' || cardType === 'virtual' ? cardType : (operation.templateId ?? 'virtual')
  const last4 = useMemo(() => card?.maskedPan.slice(-4) ?? operation.last4, [card?.maskedPan, operation.last4])
  return (
    <View style={styles.cardRow}>
      <View style={styles.cardLabelBlock}>
        <GText style={styles.rowLabel}>{t('card')}</GText>
        <GText fw={500} style={styles.rowValue}>
          {templateId === 'platinum' ? t('platinum-card') : t('virtual-card')}
        </GText>
      </View>
      <AppBankCard type={templateId} last4Digits={last4} isNumberVisible={operation.kind !== 'card-issue'} />
    </View>
  )
}

function WithdrawalRecipient({ operation }: TransactionDetailsProps) {
  const styles = useCreateStyles()
  const { t } = useI18N(locales)

  return (
    <View style={styles.detailGroup}>
      <DetailRow delay={260} label={t('bank')}>
        <View style={styles.bankValue}>
          <BankAvatar name={operation.bankName} isSmall />
          <GText fw={500} style={styles.rowValue}>
            {operation.bankName ?? '—'}
          </GText>
        </View>
      </DetailRow>
      <DetailRow delay={300} label={t('phone')} value={operation.phone ? formatPhoneDisplay(operation.phone) : '—'} />
    </View>
  )
}

function TransactionDetails({ operation }: TransactionDetailsProps) {
  const styles = useCreateStyles()
  const { t } = useI18N(locales)
  const { handleCopy } = useClipboard()
  const { kind } = operation
  const operationId = operation.id
  const displayOperationId = useMemo(
    () =>
      operationId.length > 12 ? `#${operationId.replace(/^#/, '').slice(0, 6)}…${operationId.slice(-3)}` : operationId,
    [operationId],
  )
  let status = t('status-success')
  let statusTone: ValueTone = 'success'

  if (operation.status === OperationStatus.Pending) {
    status = t('status-pending')
    statusTone = 'warning'
  }

  if (operation.status === OperationStatus.Failed) {
    status = t('status-cancelled')
    statusTone = 'critical'
  }

  return (
    <Animated.View entering={FadeInDown.delay(140).duration(420)} style={styles.details}>
      <GText fw={600} style={styles.sectionTitle}>
        {t('details')}
      </GText>
      <View style={styles.detailGroups}>
        {kind === 'card-issue' ? <CardIssueFacts operation={operation} /> : <TransactionFacts operation={operation} />}
        {kind === 'withdraw' && <WithdrawalRecipient operation={operation} />}
        <View style={styles.detailGroup}>
          <DetailRow delay={340} label={t('status')} value={status} valueTone={statusTone} />
          <DetailRow delay={380} label={t('operation-id')}>
            <Pressable accessibilityRole="button" onPress={handleCopy(operationId)} style={styles.idValue}>
              <AppIcon icon="copy-stroke" size={24} isSecondary />
              <GText fw={500} style={styles.rowValue}>
                {displayOperationId}
              </GText>
            </Pressable>
          </DetailRow>
        </View>
      </View>
    </Animated.View>
  )
}

function SupportRow() {
  const styles = useCreateStyles()
  const { t } = useI18N(locales)
  const { links } = useMobileConfig()

  return (
    <View style={styles.supportBlock}>
      <View style={styles.divider} />
      <Pressable accessibilityRole="button" onPress={handleOpenLink(links.linkSupport)} style={styles.supportRow}>
        <View style={styles.supportContent}>
          <AppIcon icon="support-headset" size={24} />
          <GText fw={500} style={styles.supportText}>
            {t('support')}
          </GText>
        </View>
        <AppIcon icon="chevron-right" size={20} isSecondary />
      </Pressable>
    </View>
  )
}

function TransactionNotice({ operation }: TransactionDetailsProps) {
  const styles = useCreateStyles()
  const { t } = useI18N(locales)
  const dispatch = useAppDispatch()
  const navigation = useNavigation()
  const isFailed = operation.status === OperationStatus.Failed
  const isIssue = operation.kind === 'card-issue'
  const isTopup = operation.kind === 'topup'

  if (
    operation.status === OperationStatus.Completed ||
    operation.kind === 'purchase' ||
    operation.kind === 'cancelled-purchase' ||
    (operation.kind === 'withdraw' && !isFailed)
  ) {
    return <View style={styles.divider} />
  }
  let title = t('issue-processing')
  let description = t('issue-wait')

  if (isFailed && isIssue) {
    title = t('issue-failed')
    description = t('issue-failed-description')
  }

  if (isFailed && isTopup) {
    title = t('topup-failed')
    description = t('topup-failed-description')
  }

  if (isFailed && operation.kind === 'withdraw') {
    title = t('withdraw-failed')
    description = t('withdraw-failed-description', {
      phone: operation.phone ? formatPhoneDisplay(operation.phone) : '—',
    })
  }
  const openQr = () => {
    if (isIssue) navigation.navigate(ScreensList.PAYMENT_LINK, { txId: operation.txId ?? operation.id })
    else if (operation.qrUrl)
      navigation.navigate(ScreensList.PAYMENT_LINK, {
        amount: operation.debitAmount,
        qrUrl: operation.qrUrl,
        expiresAt: operation.expiresAt,
      })
  }
  return (
    <StatusNotice
      state={isFailed ? 'cancelled' : 'pending'}
      title={title}
      description={operation.description ?? description}
      onPress={!isFailed && (isIssue || operation.qrUrl) ? openQr : undefined}
    >
      {isFailed && isIssue && (
        <PillButton
          title={t('retry-issue')}
          height={40}
          onPress={() => {
            const templateId = operation.templateId ?? 'virtual'
            void dispatch(issueCard({ templateId }))
            navigation.navigate(ScreensList.PAYMENT_LINK, { templateId })
          }}
        />
      )}
      {isFailed && isTopup && (
        <View style={styles.refundNote}>
          <GText variant="caption-14-regular" isSuccess>
            {operation.refundNote ?? t('refund-note')}
          </GText>
        </View>
      )}
    </StatusNotice>
  )
}

export function ScreenTxDetails() {
  const styles = useCreateStyles()
  const route = useRoute<RootRouteProps<ScreensList.TX_DETAILS>>()
  const cardIssue = useAppSelector(selectedCardIssue)
  const cards = useAppSelector(selectedCards)
  const dispatch = useAppDispatch()
  useEffect(() => {
    if (!cards) void dispatch(fetchCards())
  }, [cards, dispatch])
  const [loadedOperation, setLoadedOperation] = useState<Operation | null>(null)
  const initialOperation = route.params?.operation
  let operation = loadedOperation?.id === initialOperation?.id ? loadedOperation : initialOperation

  if (
    operation?.kind === 'card-issue' &&
    operation.status === OperationStatus.Pending &&
    cardIssue.invoice?.txId === (operation.txId ?? operation.id)
  ) {
    const invoice = cardIssue.invoice
    let status = OperationStatus.Pending

    if (invoice.state === 'completed') status = OperationStatus.Completed

    if (invoice.state === 'expired' || invoice.state === 'failed') status = OperationStatus.Failed
    operation = { ...operation, status }
  }

  // Статус обновляется каждые две секунды, пока операция обрабатывается.
  useEffect(() => {
    if (!initialOperation) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    const load = async () => {
      let shouldPoll = initialOperation.status === OperationStatus.Pending
      try {
        const response = await apiGetTransaction({ routeParams: { id: initialOperation.id } })

        if (cancelled) return
        const next = parseBackendOperation(response?.data, initialOperation)

        if (next) {
          setLoadedOperation(next)
          shouldPoll = next.status === OperationStatus.Pending

          if (!shouldPoll) {
            void dispatch(fetchTransactions())
            void dispatch(fetchCards())
          }
        }
      } catch {
        // Временная ошибка оставляет последний известный статус.
      } finally {
        if (!cancelled && shouldPoll)
          timer = setTimeout(() => {
            void load()
          }, 2000)
      }
    }
    void load()
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [dispatch, initialOperation])

  if (!operation) return null

  const createdAt = new Date(operation.createdAt)
  const date = `${createdAt.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })}, ${createdAt.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}`
  const notice = <TransactionNotice operation={operation} />
  return (
    <ScreenWrapper contentContainerStyle={styles.screen} showsVerticalScrollIndicator={false}>
      <Header isBackButton exitCross={false} title={<GText style={styles.date}>{date}</GText>} />
      <View style={styles.container}>
        <TransactionHero operation={operation} />
        {notice}
        <TransactionDetails operation={operation} />
        <SupportRow />
      </View>
    </ScreenWrapper>
  )
}
